import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { t } from "@/i18n-js/instance";
import { OtpChallengeModal } from "@circle-react/components/OtpChallengeModal";

interface OtpModalState {
  error: string | null;
  isLoading: boolean;
  isOpen: boolean;
}

type RetryWithCode<T> = (code: string) => Promise<T>;

interface OtpChallengeController {
  runMutationWithOtp: <T>(retryWithOtp: RetryWithCode<T>) => Promise<T>;
}

const OtpChallengeContext = createContext<OtpChallengeController | null>(null);
OtpChallengeContext.displayName = "OtpChallengeContext";

export const useOtpChallengeContext = () => {
  const ctx = useContext(OtpChallengeContext);
  if (!ctx) throw new Error("OtpChallengeProvider is missing");
  return ctx;
};

type OtpPromise = {
  retryWithOtp: (code: string) => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
} | null;

interface OtpChallengeProviderProps {
  children: React.ReactNode;
}

export const OtpChallengeProvider = ({
  children,
}: OtpChallengeProviderProps) => {
  const [state, setState] = useState<OtpModalState>({
    error: null,
    isLoading: false,
    isOpen: false,
  });

  const mutationWithOtpRef = useRef<OtpPromise>(null);

  const close = useCallback(() => {
    setState({ isOpen: false, isLoading: false, error: null });
    mutationWithOtpRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    const mutationWithOtp = mutationWithOtpRef.current;

    if (mutationWithOtp) {
      mutationWithOtp.reject(new Error(t("otp_challenge_modal.cancelled")));
    }

    close();
  }, [close]);

  const runMutationWithOtp = useCallback(
    <T,>(retryWithOtp: RetryWithCode<T>) =>
      new Promise<T>((resolve, reject) => {
        mutationWithOtpRef.current = {
          retryWithOtp: (code: string) =>
            retryWithOtp(code) as Promise<unknown>,
          resolve: (value: unknown) => resolve(value as T),
          reject,
        };

        setState({ isOpen: true, isLoading: false, error: null });
      }),
    [],
  );

  const handleSubmit = useCallback(
    async (code: string) => {
      const mutationWithOtp = mutationWithOtpRef.current;
      if (!mutationWithOtp) return;

      setState(prevState => ({ ...prevState, isLoading: true, error: null }));

      try {
        const result = await mutationWithOtp.retryWithOtp(code.trim());

        mutationWithOtp.resolve(result);

        close();
      } catch (err: unknown) {
        setState({ error: null, isLoading: false, isOpen: false });
        mutationWithOtp.reject(err);
        mutationWithOtpRef.current = null;
      }
    },
    [close],
  );

  const value = useMemo(
    () => ({
      runMutationWithOtp,
    }),
    [runMutationWithOtp],
  );

  return (
    <OtpChallengeContext.Provider value={value}>
      {children}

      <OtpChallengeModal
        isOpen={state.isOpen}
        isLoading={state.isLoading}
        error={state.error || undefined}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </OtpChallengeContext.Provider>
  );
};
